import {
    AnimationBaseController,
    AnimationTextStyle,
    IAnimationControllerConfiguration,
    Inject,
    ModelEvents,
    SoundType,
} from '@denishussar/html5-game-engine';
import { GameModel } from '../../Models/GameModel';
import { GameLogic } from '../../Logic/GameLogic';
import { getCarConfig, getPaintConfig, getUpgradeConfig } from '../../Common/GameConfig';
import { Sounds } from '../../Constants/Sounds';
import { SoundModel } from '../../Models/SoundModel';
import { GarageLogic } from '../../Logic/GarageLogic';
import { CarModel } from '../../Models/CarModel';
import { GarageEvents } from '../../Constants/Events';
import { shorterNumber, thousandSpacing } from '../../Utils/StringRework';
import { LocalizationContents } from '../../LocalizationContents';
import { filters, Text } from 'pixi.js';

export interface IGarageScreenTabShopItemControllerOptions {
    index: number;
    type: GarageSlideEnum;
    state: StateEnum;
    onClick: (index: number) => void;
}

export enum StateEnum {
    selectedUnlocked,
    unlocked,
    locked,
    selectedLocked,
    upgradeSoon,
    notAvailable,
}

export enum GarageSlideEnum {
    car,
    upgrade,
    paint
}

const blur = new filters.BlurFilter(4);
blur.blur = 20;
blur.passes = 1;


export class GarageScreenTabShopItemController extends AnimationBaseController {
    $model: GameModel;

    private _state: StateEnum = StateEnum.locked;
    private _previousState: StateEnum = StateEnum.locked;

    public constructor(
        @Inject private options: IGarageScreenTabShopItemControllerOptions,
        @Inject private gameLogic: GameLogic,
        @Inject private garageLogic: GarageLogic,
        @Inject private modelEvents: ModelEvents,
    ) {
        super('GarageScreenTabShopItemController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        let watches = {};
        let events = {};
        switch (this.options.type) {
            case GarageSlideEnum.car:
                this.ifCarPreparations(watches, events);
                break;
            case GarageSlideEnum.upgrade:
                this.ifUpgradePrepation(watches, events);
                break;
            case GarageSlideEnum.paint:
                this.ifPaintPreparation(watches, events);
                break;
        }
        return <IAnimationControllerConfiguration>{
            childControllers: {},
            sounds: [
                {
                    id: Sounds.BUTTON,
                    type: SoundType.SFX,
                },
            ],
            modelWatch: watches,
            modelEvents: events,
            onPointerDown: () => {
                if (this.options.onClick) {
                    this.soundManager.play(Sounds.BUTTON, false, SoundModel.UI);
                    this.options.onClick(this.options.index);
                }
            },
        };
    }

    protected onInitialize(): void {
        switch (this.options.type) {
            case GarageSlideEnum.car:
                this.ifCar();
                break;
            case GarageSlideEnum.upgrade:
                this.ifUpgrade();
                break;
            case GarageSlideEnum.paint:
                this.ifPaint();
                break;

        }
    }

    

    private ifCarPreparations(watches: Object, events: Object) {
        watches["garage_selectedCar"] = (car: CarModel) => {
            this.ifCar();
        };
        events[GarageEvents.buyCar] = () => {
            this.ifCar();
        };
        events[GarageEvents.buyPaint] = () => this.ifCar();
    }

    private ifCar() {
        if (this.$model.garage_selectedCar != this.options.index) {
            this.gotoAndStopByQuery('little_cars', 'on');
            if (this.$model.cars[this.options.index].isBought)
                this.gotoAndStop(StateEnum.unlocked);
            else {
                this.gotoAndStop(StateEnum.locked);
            }
        }
        else
            if (this.$model.cars[this.options.index].isBought) {
                this.gotoAndStop(StateEnum.selectedUnlocked);
            }
            else {
                this.gotoAndStop(StateEnum.selectedLocked);
            }

        if (this.$model.cars[this.options.index].isBought)
            this.gotoAndStopByQuery('little_cars', 0);
        else
            this.gotoAndStopByQuery('little_cars', 1);

        this.gotoAndStopByQuery('little_cars.little_cars_off', this.options.index);
        this.gotoAndStopByQuery('little_cars.little_cars_on', this.options.index);
        this.gotoAndStopByQuery('little_cars.little_cars_on.little_icon', this.$model.cars[this.options.index].paint);

        this.setTextByQuery('name_txt', LocalizationContents.getText(this.$model.cars[this.options.index].name));
        if (this.$model.cars[this.options.index].price < 100000)
            this.setTextByQuery('txt_price', thousandSpacing(this.$model.cars[this.options.index].price));
        else
            this.setTextByQuery('txt_price', shorterNumber(this.$model.cars[this.options.index].price));
    }

    private ifUpgradePrepation(watches: Object, events: Object) {
        watches["currentPlayer"] = (car: CarModel) => {
            this.ifUpgrade();
        }
        watches["garage_selectedUpgrade"] = (upgrade: number) => {
            this.ifUpgrade();
        }
        events[GarageEvents.buyCar] = () => {
            this.ifUpgrade();
        }
        events[GarageEvents.buyUpgrade] = () => {
            this.ifUpgrade();
        }
    }

    private updateUpgradeIcon(locked) {
        this.gotoAndStopByQuery('icon_improvements', locked ? 1 : 0);
        this.gotoAndStopByQuery('icon_improvements.improvements', this.options.index);

    }

    private ifUpgrade() {
        if (this.options.index >= this.$model.garage_maxUpgrades) {
            this.setTextStyleByQuery("button_new_features", {}, {
                b: {
                    lineHeight: 50,
                }
            })
            this.setTextByQuery("button_new_features", "<b>" + LocalizationContents.getText("button_new_features") + "</b>");
            
            this.setTextByQuery("button_new_features_blur", LocalizationContents.getText("button_new_features"));
            (this.getDisplayObjectByQuery("button_new_features_blur")[0]).filters = [blur];
            this.setTextVerticalAlignByQuery("button_new_features", "middle");
            this.setTextVerticalAlignByQuery("button_new_features_blur", "middle");
            

            this.gotoAndStop(StateEnum.upgradeSoon);
            return;
        }
        if (!this.$model.currentPlayer.isBought) {
            this.gotoAndStop(StateEnum.notAvailable);
            this.updateUpgradeIcon(true);

            return;
        }
        if (this.$model.garage_selectedUpgrade != this.options.index) {
            if (this.$model.currentPlayer.upgrades[this.options.index]) {
                this.gotoAndStop(StateEnum.unlocked);
            } else {
                this.gotoAndStop(StateEnum.locked);

            }
        }
        else
            if (this.$model.currentPlayer.upgrades[this.options.index]) {
                this.gotoAndStop(StateEnum.selectedUnlocked);
            } else {
                this.gotoAndStop(StateEnum.selectedLocked);

            }

        this.updateUpgradeIcon(false);
        this.setTextByQuery('txt_price', thousandSpacing(getUpgradeConfig(this.options.index, 'price')));
    }

    private ifPaintPreparation(watches: Object, events: Object) {
        watches["currentPlayer"] = (car: CarModel) => {
            this.ifPaint();
        }
        watches["garage_selectedPaint"] = (upgrade: number) => {
            this.ifPaint();
        }
        events[GarageEvents.buyCar] = () => {
            this.ifPaint();
        }
        events[GarageEvents.buyPaint] = () => {
            this.ifPaint();
        }
    }

    private ifPaint() {
        if (!this.$model.currentPlayer.isBought) {
            this.gotoAndStop(StateEnum.notAvailable);
            return;
        }
        if (this.$model.garage_selectedPaint != this.options.index) {
            if (this.$model.currentPlayer.availablePaints[this.options.index]) {
                this.gotoAndStop(StateEnum.unlocked);
            } else {
                this.gotoAndStop(StateEnum.locked);
                this.setTextByQuery('txt_price', thousandSpacing(getPaintConfig(this.options.index, 'price')));
            }
        }
        else
            if (this.$model.currentPlayer.availablePaints[this.options.index]) {
                this.gotoAndStop(StateEnum.selectedUnlocked);
            } else {
                this.gotoAndStop(StateEnum.selectedLocked);
                this.setTextByQuery('txt_price', thousandSpacing(getPaintConfig(this.options.index, 'price')));
            }
        this.gotoAndStopByQuery('icon_color', this.options.index);
    }

    protected onResize(width: number, height: number, scale: number): void {
    }

    protected onUpdate(frameRate: number): void {
    }

    public getState(): StateEnum {
        return this._state;
    }

    public getPreviousState(): StateEnum {
        return this._previousState;
    }
}
